import React, { useRef, useState, useMemo, useCallback, useEffect, useContext } from "react";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import CampaignForm from "../../components/Form/CampaignForm";
import ReusableTable from "../../components/UI/ReusableTable";
import Drawer from "../../components/UI/Drawer";
import CampaignDetails from "../../components/Details/CampaignDetails";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import "react-loading-skeleton/dist/skeleton.css";
import ReactPaginate from 'react-paginate';
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { MoreVertical, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';

const Campaigns = () => {
  const {
    campaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    loading,
    pagination,
    fetchCampaigns,
    campaignLoading,
  } = useCampaigns();

  const buttonRefs = useRef({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const { roles } = useContext(AuthContext);
  const isHR = roles.includes("HR");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCampaignID, setSelectedCampaignID] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [copied, setCopied] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const campaignId = params.get("campaign_id");
    if (campaignId && campaigns.length > 0) {
      const found = campaigns.find(c => String(c.id) === String(campaignId));
      if (found) {
        setSelectedCampaign(found);
        setIsDrawerOpen(true);
      }
    }
  }, [location.search, campaigns]);

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 200;
      const dropdownHeight = 180;
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4;
      if (x < 0) x = rect.left - dropdownWidth;
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4;
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      if (x < 0) x = 8;
      if (y < 0) y = 8;
      setDropdownCoords({ x, y });
    }
  };

  // copy to clipboard button
  const handleCopy = async (campaign_id) => {
    const domain = window.location.origin;
    try {
      await navigator.clipboard.writeText(`${domain}/campaign/${campaign_id}`);
      setCopied(campaign_id);
      setNotification({message: 'Campaign link copied to clipboard!', type: 'success'});
      setTimeout(() => {
        setCopied(false);
        setNotification('');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setNotification('Failed to copy the link. Please try again.');
    }
  };

  // Handler to update campaign status
  const handleStatusChange = useCallback(async (campaign, newStatus) => {
    try {
      const response = await updateCampaign({ id: campaign.id, status: newStatus });
      // console.log("response: ", response)
      if (response?.success) {
        setNotification({ message: "Status updated successfully", type: "success" });
      } else {
        setNotification({ message: response?.message || "Failed to update status", type: "error" });
      }
    } catch (error) {
      setNotification({ message: "Failed to update status", type: "error" });
    }
  }, [updateCampaign]);

  const openModalForAdd = () => {
    setEditingCampaign(null);
    setErrors({});
    setIsModalOpen(true);
  };

  const openModalForEdit = (campaign) => {
    setEditingCampaign(campaign);
    setErrors({});
    setIsModalOpen(true);
  };

  const openDrawerForDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCampaign(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCampaign(null);
    setErrors({});
  };

  const handleFormSubmit = async (campaign) => {
    try {
      if (editingCampaign) {
        await updateCampaign({ ...editingCampaign, ...campaign });
        setNotification({
          message: "Campaign updated successfully",
          type: "success"
        });
      } else {
        const response = await addCampaign(campaign);
        if (!response || response.success === false) {
          setNotification({
            message: `${response?.message || "Failed to add campaign. Please try again."}`,
            type: "error"
          });
          return;
        }
        setNotification({
          message: "Campaign added successfully",
          type: "success"
        });
      }
      closeModal();
    } catch (error) {
      if (error?.errors) {
        setErrors(error.errors);
        setNotification({
          message: error.errors,
          type: "error"
        });
      } else {
        setNotification({
          message: `"An unexpected error occurred:", ${error}`,
          type: "error"
        });
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      setNotification({
        message: "Campaign deleted successfully",
        type: "success"
      });
    } catch (error) {
      setNotification({
        message: "Failed to delete campaign",
        type: "error"
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchCampaigns(newPage);
    }
  };
  
  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => {
      const baseColumns = [
        {
          label: "Sr. No",
          field: "sr_no",
          render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
        },
        {
          label: "Campaign Name",
          field: "campaign_name",
          render: (row) => (
            <div className="flex items-center gap-1">
              <button
                onClick={() => openDrawerForDetails(row)}
                className="text-blue-600 hover:underline dark:text-white hover:underline-offset-4"
                aria-label={`View details for ${row.campaign_name}`}>
                {row.campaign_name}
              </button>
            </div>
          ),
        },
        {
          label: "Copy Link",
          field: "copy_link",
          render: (row) => (
            <button
              title="Copy campaign link"
              onClick={() => handleCopy(row.id)}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm disabled:opacity-60"
              disabled={copied === row.id}
            >
              {copied === row.id ? "Copied" : "Copy Link"}
            </button>
          ),
        },
        {
          label: "Status",
          field: "status",
          render: (row) => (
            row.status === "Expired" ? (
              <span className="px-[26px] py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium">
                Expired
              </span>
            ) : isHR ? (
              <div className="relative">
                <select
                  value={row.status}
                  onChange={e => handleStatusChange(row, e.target.value)}
                  className="px-[15px] py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm disabled:opacity-60"
                  disabled={campaignLoading === row.id}
                >
                  <option value="Open">Open</option>
                  <option value="Close">Close</option>
                  <option value="On Hold">On Hold</option>
                </select>
                {campaignLoading === row.id && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                  </span>
                )}
              </div>
            ) : (
              <span className={`px-[26px] py-1 rounded text-sm font-medium ${
                row.status === "Active" 
                  ? "bg-green-200 text-green-700 dark:bg-green-700 dark:text-green-300" 
                  : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              }`}>
                {row.status}
              </span>
            )
          ),
        },
        // { label: "Job Description", field: "job_description" },
        { label: "Duration (Days)", field: ["campaign_running_days"] },
      ];

      // Only add Actions column if user is HR
      if (isHR) {
        baseColumns.push({
          label: "Actions",
          field: "actions",
          render: (row) => (

            <div className="flex items-center justify-center">
              <div className="relative dropdown-container">
                <button
                  ref={el => { if (el) buttonRefs.current[row.id] = el; }}
                  onClick={e => handleDropdownToggle(row.id, e)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {openDropdownId === row.id && createPortal(
                <div
                  style={{
                    position: 'fixed',
                    top: dropdownCoords.y,
                    left: dropdownCoords.x,
                    zIndex: 9999,
                  }}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[200px]"
                >
                  <div className="py-1 flex flex-col space-y-0">
                    {row.status !== "Close" && (
                      <button
                        onClick={() => {openModalForEdit(row); setOpenDropdownId(null);}}
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                        aria-label={`Edit ${row.campaign_name}`}
                      >
                        Edit
                      </button>
                    )}
                    <DeleteButton style={`text-left text-red-600 text-sm hover:text-red-800 px-3 py-2`} onDelete={() => {handleDelete(row.id); setOpenDropdownId(null);}} />
                  </div>
                </div>,
                document.body
              )}
            </div>
          ),
        });
      }

      return baseColumns;
    };
  }, [openDrawerForDetails, handleDelete, openModalForEdit, handleStatusChange, campaignLoading, copied, isHR]);

  // Columns to exclude from filtering
  const excludeFilterColumns = useMemo(() => ["actions", "job_description", "copy_link"], []);

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Campaigns</h2>
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto">
          Create New Campaign
        </button>
      </div>

      {/* Campaigns Content */}
        <div>
          <ReusableTable
            data={campaigns}
            columns={getColumns(currentPage, pagination.perPage)}
            filterable={true}
            loading={loading}
            excludeFilterColumns={excludeFilterColumns}
            onFilterChange={(filtering) => setIsFiltering(filtering)}
          />

          {/* Pagination */}
          {!isFiltering && campaigns?.length > 0 && (
            <ReactPaginate
              breakLabel="..."
              onPageChange={(event) => handlePageChange(event.selected + 1)}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              pageCount={pagination.totalPages}
              renderOnZeroPageCount={null}
              nextLabel="Next >"
              previousLabel="< Prev"
              previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
              nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
              containerClassName="flex items-center justify-center mt-4"
              pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
            />
          )}
        </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingCampaign ? "Edit Campaign" : "Create New Campaign"}
        fullSize={false}>

        <CampaignForm
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingCampaign || {}}
          mode={editingCampaign ? "Edit" : "Add"}
        />
      </Modal>

      {/* Drawer for Campaign Details */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        border_radius="md:rounded-none"
        showClose={false}
        ChilDiv=""
        ChildParent="">

        <CampaignDetails
          campaign={selectedCampaign}
          campaign_id={selectedCampaign?.id}
          close={closeDrawer}
        />
      </Drawer>
    </div>
  );
};

export default React.memo(Campaigns);
